<template>
  <div class="text-base py-1">
    <!-- Admin View section -->
    <template v-if="adminEntitiesGrouped.length > 0">
      <template v-for="group in adminEntitiesGrouped" :key="group.root.id">
        <!-- Root header -->
        <div
          class="flex items-center px-3 py-2 cursor-pointer hover:bg-blue-alpha"
          @click.stop="toggleRoot(group.root.id)"
        >
          <Avatar
            :entity="{ id: group.root.id, text: group.root.name }"
            class="mr-2 shrink-0"
          />
          <span class="truncate font-medium grow">{{ group.root.name }}</span>
          <SvgIcon
            icon="ArrowDown"
            class="text-secondary shrink-0 ml-2 w-2.5 h-2.5 transition-transform"
            :class="{ 'rotate-180': expandedRootId === group.root.id }"
          />
        </div>
        <!-- Entities under this root -->
        <ExpandTransition :show="expandedRootId === group.root.id">
          <SessionOption
            v-for="entityItem in group.entities"
            :id="`select-admin-${entityItem.id}`"
            :key="entityItem.id"
            icon="Box"
            :label="entityItem.name"
            :active="
              currentSession?.role === 'admin' &&
              currentSession?.entity_id === entityItem.id
            "
            nested
            @click="selectSession({ role: 'admin', entity_id: entityItem.id })"
          />
        </ExpandTransition>
      </template>
    </template>

    <!-- Separator -->
    <hr
      v-if="
        adminEntitiesGrouped.length > 0 &&
        (hasAnyClientRole || hasAnyReferralRole)
      "
      class="border-t border-outline my-2"
    />

    <!-- Client View option -->
    <SessionOption
      v-if="hasAnyClientRole"
      id="select-client-view"
      icon="Users"
      :label="$t('ui.auth.client_view')"
      :active="currentSession?.role === 'client'"
      @click="selectSession({ role: 'client' })"
    />

    <!-- Referral View option -->
    <SessionOption
      v-if="hasAnyReferralRole"
      id="select-referral-view"
      icon="UserCoins"
      :label="$t('ui.auth.referral_view')"
      :active="currentSession?.role === 'referral'"
      @click="selectSession({ role: 'referral' })"
    />

    <!-- Separator before assistant section -->
    <hr
      v-if="
        hasAssistantRelation &&
        (hasAnyClientRole ||
          hasAnyReferralRole ||
          adminEntitiesGrouped.length > 0)
      "
      class="border-t border-outline my-2"
    />

    <!-- Assistant client items -->
    <SessionOption
      v-for="client in assistantClientsWithEntity"
      :key="client.relation_id"
      icon="UserCog"
      :label="`${getTextFromRootUser(client.root_user)} - ${client.entity_name}`"
      :active="activeAssistantClient?.relation_id === client.relation_id"
      @click="selectAssistantClient(client)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { getTextFromRootUser } from '@/helpers/entity'
import Avatar from '@/components/misc/Avatar'
import ExpandTransition from '@/components/misc/ExpandTransition'
import SvgIcon from '@/components/misc/SvgIcon'
import SessionOption from './SessionOption.vue'
import type {
  AssistantClientFlatItem,
  Entity,
  Root,
} from '@/types/services/root'
import type { SessionRole, UserSession } from '@/types/services/user'

interface AdminEntitiesGroup {
  root: Root
  entities: Entity[]
}

const props = defineProps<{
  currentSession?: UserSession | null
}>()

const emit = defineEmits<{
  select: [session: UserSession]
}>()

const expandedRootId = ref<string | null>(null)

const router = useRouter()
const userStore = useUserStore()
const {
  access_data,
  hasAnyClientRole,
  hasAnyReferralRole,
  entityId,
  assistantClientsWithEntity,
  hasAssistantRelation,
  activeAssistantClient,
  findRootIdByEntityId,
} = storeToRefs(userStore)

const adminEntitiesGrouped = computed<AdminEntitiesGroup[]>(() => {
  const groups: Map<string, AdminEntitiesGroup> = new Map()

  for (const rad of access_data.value) {
    const adminEntities: Entity[] = []

    for (const ead of rad.entity_access_list) {
      const hasAdminRole =
        rad.root_user.is_superadmin ||
        ead.relations.some((r) => r.role === 'admin')

      if (hasAdminRole) {
        adminEntities.push(ead.entity)
      }
    }

    if (adminEntities.length > 0) {
      groups.set(rad.root.id, {
        root: rad.root,
        entities: adminEntities.sort((a, b) => a.name.localeCompare(b.name)),
      })
    }
  }

  return Array.from(groups.values()).sort((a, b) =>
    a.root.name.localeCompare(b.root.name)
  )
})

function selectSession(session: UserSession) {
  emit('select', session)
}

function selectAssistantClient(client: AssistantClientFlatItem) {
  emit('select', {
    role: 'assistant' as SessionRole,
    entity_id: client.fund_entity_id,
    client_relation_id: client.relation_id,
  })
  router.push({
    name: 'AssistantFundDetail',
    params: { client_relation_id: client.relation_id },
  })
}

function toggleRoot(rootId: string) {
  expandedRootId.value = expandedRootId.value === rootId ? null : rootId
}

watch(
  () => props.currentSession,
  (currentSession) => {
    const _entityId = currentSession?.entity_id ?? entityId.value
    if (!_entityId) {
      return
    }

    const rootId = findRootIdByEntityId.value(_entityId)
    if (rootId) {
      expandedRootId.value = rootId
    }
  },
  { immediate: true, deep: true }
)
</script>
